import { FC, useMemo } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@utils/cn';
import { PASSWORD_POLICY_RULES } from '@utils/passwordPolicy';
import { useTranslation } from '@hooks/useTranslation';

export interface PasswordRequirementsProps {
  /** Current password value being validated. */
  value: string;
  /**
   * Force visibility. When omitted, the box appears automatically once the
   * user starts typing (value is not empty).
   */
  visible?: boolean;
  className?: string;
}

/**
 * Sprint 47, story-233. The checklist rows come straight from `PASSWORD_POLICY_RULES`, the same
 * array the Yup validators are built from, so a row can never read "met" while the form still
 * refuses the value (the 0FJwCeJe defect). Labels are localized (en + es).
 */
export const PasswordRequirements: FC<PasswordRequirementsProps> = ({
  value,
  visible,
  className,
}) => {
  const tPolicy = useTranslation('passwordPolicy');
  const shouldShow = visible ?? value.length > 0;

  const checks = useMemo(
    () =>
      PASSWORD_POLICY_RULES.map((rule) => ({
        key: rule.key,
        label: tPolicy[rule.labelKey],
        met: rule.test(value),
      })),
    [tPolicy, value],
  );

  if (!shouldShow) return null;

  const allMet = checks.every((item) => item.met);

  return (
    <div
      data-testid="password-requirements"
      className={cn(
        'rounded-lg border p-3 bg-gray-50 dark:bg-neutral-800/40',
        allMet
          ? 'border-green-300 dark:border-green-700/40'
          : 'border-gray-200 dark:border-neutral-700',
        className,
      )}>
      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
        {tPolicy.checklistTitle}
      </p>
      <ul className="space-y-1">
        {checks.map((item) => (
          <li
            key={item.key}
            data-testid={`password-rule-${item.key}`}
            data-met={item.met}
            className="flex items-center gap-2 text-xs">
            {item.met ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <Circle className="w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500" />
            )}
            <span
              className={cn(
                item.met
                  ? 'font-medium text-green-700 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400',
              )}>
              {item.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordRequirements;
